// Shared pure logic for the type-to-filter combobox (components/SearchableSelect.jsx via components/comboboxLogic.js)
// and the Skills → Quality & Improve agent/dataset pickers. Kept out of the JSX
// shell so the filter + keyboard-highlight math stay 100% unit-covered.
//
// This mirrors the proven eval-screen logic
// (features/analytics/observability/evalRunFormat.js: filterAgents / nextHighlight,
// both 100% covered), generalized from the agent-only shape to {id, label}
// options so more than one surface can reuse it. The eval screen keeps its own
// copies untouched (its tests pin them), per the "replicate, don't risk the
// green suite" call in the task.

// Case-insensitive type-to-filter over combobox options. Matches the visible
// `label` first, then falls back to the `id` (so pasting an id also finds it).
// A blank query returns the full (non-null) list; a non-array input returns [].
export function filterOptions(options, query) {
  const list = Array.isArray(options) ? options.filter(Boolean) : [];
  const q = String(query == null ? "" : query).trim().toLowerCase();
  if (!q) return list;
  // `list` is already Boolean-filtered above, so every `o` here is truthy.
  return list.filter((o) => {
    const label = String(o.label == null ? "" : o.label).toLowerCase();
    const id = String(o.id == null ? "" : o.id).toLowerCase();
    return label.includes(q) || id.includes(q);
  });
}

// Keyboard highlight movement for a listbox of `count` options. `current` may be
// -1 (nothing highlighted yet); `delta` is +1 (ArrowDown) or -1 (ArrowUp). Wraps
// at the ends; count 0 → -1 (nothing to highlight); junk count → -1.
export function nextHighlight(current, delta, count) {
  const n = Number.isFinite(Number(count)) ? Number(count) : 0;
  if (n <= 0) return -1;
  const cur = Number.isInteger(current) ? current : -1;
  if (cur < 0) return delta > 0 ? 0 : n - 1;
  return (cur + delta + n) % n;
}

// Normalize a raw list (agents / datasets shaped as {id, name}) into combobox
// options [{id, label, raw}]. `labelKeys` are tried in order; falls back to the
// id when none is present. Skips null entries.
export function toOptions(list, labelKeys = ["name", "label"]) {
  const keys = Array.isArray(labelKeys) ? labelKeys : [labelKeys];
  return (Array.isArray(list) ? list : [])
    .filter(Boolean)
    .map((x) => {
      let label;
      for (const k of keys) {
        if (x[k]) { label = x[k]; break; }
      }
      return { id: x.id, label: label || x.id, raw: x };
    });
}
